import React, { useState, useEffect } from "react";
import { productAPI, supplierAPI, purchaseAPI } from "../api/apiService";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, Package, Search, ArrowLeft, History, FileText, CheckCircle, Clock, RefreshCw, Eye, Edit, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useContext } from "react";
import { NotificationContext } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../context/PermissionsContext";
import EmptyState from "../components/EmptyState";

export default function StockInwardPage() {
    const navigate = useNavigate();
    const { userRole } = useAuth();
    const { hasPermission } = usePermissions();
    const { addNotification } = useContext(NotificationContext);

    // Tab State
    const [activeTab, setActiveTab] = useState("new"); // "new" or "history"

    // Data State
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [selectedSupplier, setSelectedSupplier] = useState("");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [cart, setCart] = useState([]); // { product, quantity, purchasePrice, sellingPrice }
    const [productSearch, setProductSearch] = useState("");

    // Modal State
    const [viewModalData, setViewModalData] = useState(null);
    const [loadingView, setLoadingView] = useState(false);
    const [editModalData, setEditModalData] = useState(null);
    const [editForm, setEditForm] = useState({ invoice_number: "", paid_amount: "", payment_status: "pending" });
    const [savingEdit, setSavingEdit] = useState(false);
    const [variantModalProduct, setVariantModalProduct] = useState(null); // For variant selection
    const [successModalData, setSuccessModalData] = useState(null); // For custom confirmation modal
    const [alertMessage, setAlertMessage] = useState(null); // For custom alert modal

    useEffect(() => {
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch history when tab changes to history
    useEffect(() => {
        if (activeTab === "history") {
            fetchHistory();
        }
    }, [activeTab]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [suppRes, prodRes] = await Promise.all([
                supplierAPI.getAllSuppliers(),
                productAPI.getAllProducts()
            ]);

            const suppData = suppRes.data?.results || suppRes.data || [];
            const prodData = prodRes.data?.results || prodRes.data || [];

            setSuppliers(suppData);
            setProducts(prodData);
        } catch (error) {
            addNotification("error", "Error", "Failed to load initial data");
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const response = await purchaseAPI.getAllPurchases();
            const data = response.data?.results || response.data || [];
            // Filter only received/completed orders if needed, or show all
            setHistory(data);
        } catch (error) {
            console.error("Failed to fetch history:", error);
            addNotification("error", "Error", "Failed to load history");
        } finally {
            setLoading(false);
        }
    };

    const addToCart = (product, variant = null) => {
        // If product has variants but no variant is selected yet, open modal
        if (product.has_variants && !variant && product.variants?.length > 0) {
            setVariantModalProduct(product);
            return;
        }

        // Check for duplicates (Product + Variant combination)
        const isDuplicate = cart.find(item => 
            item.product.id === product.id && 
            (variant ? item.variant?.id === variant.id : !item.variant)
        );

        if (isDuplicate) {
            addNotification("info", "Already Added", `${product.name} ${variant ? `(${variant.name})` : ""} is already in the list.`);
            return;
        }

        const newItem = {
            product: product,
            variant: variant,
            quantity: 1,
            purchasePrice: variant ? (variant.purchase_price || 0) : (product.purchase_price || 0),
            sellingPrice: variant ? (variant.unit_price || 0) : (product.unit_price || 0)
        };
        setCart([...cart, newItem]);
        setProductSearch("");
        setVariantModalProduct(null);
    };

    const updateCartItem = (index, field, value) => {
        const newCart = [...cart];
        newCart[index][field] = value;
        setCart(newCart);
    };

    const removeCartItem = (index) => {
        const newCart = cart.filter((_, i) => i !== index);
        setCart(newCart);
    };

    const handleSubmit = async () => {
        if (!selectedSupplier) {
            setAlertMessage("Please select a supplier");
            return;
        }
        if (!invoiceNumber) {
            setAlertMessage("Please enter Purchase Invoice Number");
            return;
        }
        if (cart.length === 0) {
            setAlertMessage("Please add at least one product");
            return;
        }

        const payload = {
            supplier_id: selectedSupplier,
            invoice_number: invoiceNumber,
            items: cart.map(item => ({
                product_id: item.product.id,
                variant_id: item.variant?.id || null,
                quantity: parseInt(item.quantity) || 0,
                purchasePrice: parseFloat(item.purchasePrice) || 0,
                sellingPrice: parseFloat(item.sellingPrice) || 0
            }))
        };

        const invalidItem = payload.items.find(i => i.quantity <= 0);
        if (invalidItem) {
            setAlertMessage("Quantity must be greater than 0 for all items");
            return;
        }

        setSubmitting(true);
        try {
            const response = await purchaseAPI.directStockInward(payload);
            if (response.status === 201 || response.data) {
                addNotification("success", "Stock Inward Successful", `Stock added. PO Number: ${response.data.po_number}`);
                setSuccessModalData({
                    po_number: response.data.po_number
                });
            }
        } catch (error) {
            console.error(error);
            const msg = error.response?.data?.detail || "Failed to process stock inward";
            addNotification("error", "Error", msg);
        } finally {
            setSubmitting(false);
        }
    };

    const filteredProducts = productSearch
        ? products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.product_code && p.product_code.toLowerCase().includes(productSearch.toLowerCase())))
        : [];

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this entry? This action cannot be undone.")) {
            return;
        }

        try {
            await purchaseAPI.deletePurchase(id);
            addNotification("success", "Deleted", "Stock inward entry deleted successfully");
            fetchHistory(); // Refresh list
        } catch (error) {
            console.error("Delete failed:", error);
            addNotification("error", "Error", "Failed to delete entry");
        }
    };

    const handleViewItems = async (po) => {
        setLoadingView(true);
        try {
            const res = await purchaseAPI.getPurchase(po.id);
            setViewModalData(res.data);
        } catch (err) {
            addNotification("error", "Error", "Failed to load purchase details");
        } finally {
            setLoadingView(false);
        }
    };

    const openEditModal = (po) => {
        setEditModalData(po);
        setEditForm({
            invoice_number: po.invoice_number || "",
            paid_amount: parseFloat(po.paid_amount) || 0,
            payment_status: po.payment_status || "pending",
        });
    };

    const handleSaveEdit = async () => {
        setSavingEdit(true);
        try {
            const payload = {
                invoice_number: editForm.invoice_number,
                paid_amount: parseFloat(editForm.paid_amount) || 0,
                payment_status: editForm.payment_status,
            };
            await purchaseAPI.updatePurchase(editModalData.id, payload);
            addNotification("success", "Success", "Inward updated successfully");
            setEditModalData(null);
            fetchHistory();
        } catch (err) {
            addNotification("error", "Error", err.response?.data?.detail || "Failed to update inward");
        } finally {
            setSavingEdit(false);
        }
    };

    if (!hasPermission(userRole, 'create_inventory')) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-red-500 font-bold">Access Denied. You do not have permission to manage Stock Inward.</p>
            </div>
        )
    }

    const totalAmount = cart.reduce((sum, item) => sum + (item.quantity * item.purchasePrice), 0);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-dark-bg p-4 md:p-8 transition-colors">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <ArrowLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Stock Inward</h1>
                            <p className="text-gray-600 dark:text-gray-400">Manage purchase entries and history</p>
                        </div>
                    </div>
                </div>

                {/* Tags/Tabs */}
                <div className="flex gap-4 border-b border-gray-200 dark:border-dark-border mb-6">
                    <button
                        onClick={() => setActiveTab("new")}
                        className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === "new"
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            }`}
                    >
                        <Plus className="w-4 h-4" />
                        New Entry
                    </button>
                    <button
                        onClick={() => setActiveTab("history")}
                        className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === "history"
                            ? "border-blue-600 text-blue-600 dark:text-blue-400"
                            : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            }`}
                    >
                        <History className="w-4 h-4" />
                        Inward History
                    </button>
                </div>

                {/* Content */}
                {activeTab === "new" ? (
                    /* New Entry Form */
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="flex justify-end mb-4">
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className={`px-6 py-3 rounded-lg font-bold text-white flex items-center gap-2 shadow-lg ${submitting ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                            >
                                <Save className="w-5 h-5" />
                                {submitting ? "Saving..." : "Save Entry"}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Left: Details */}
                            <div className="md:col-span-3 lg:col-span-1 space-y-6">
                                <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
                                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Invoice Details</h2>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Supplier <span className="text-red-500">*</span></label>
                                            <select
                                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:ring-2 focus:ring-blue-500"
                                                value={selectedSupplier}
                                                onChange={e => setSelectedSupplier(e.target.value)}
                                            >
                                                <option value="">Select Supplier</option>
                                                {suppliers.map(s => (
                                                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                                                ))}
                                            </select>
                                            {suppliers.length === 0 && !loading && <button onClick={() => navigate('/suppliers')} className="text-xs text-blue-500 hover:underline mt-1">Add new supplier</button>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Purchase Invoice No <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-white focus:ring-2 focus:ring-blue-500"
                                                placeholder="e.g. INV-2024-001"
                                                value={invoiceNumber}
                                                onChange={e => setInvoiceNumber(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-xl border border-blue-100 dark:border-blue-900/30">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-gray-600 dark:text-gray-300">Total Items</span>
                                        <span className="font-bold text-gray-900 dark:text-white">{cart.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xl font-bold">
                                        <span className="text-blue-900 dark:text-blue-300">Total Amount</span>
                                        <span className="text-blue-700 dark:text-blue-400">₹{totalAmount.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Right: Products */}
                            <div className="md:col-span-3 lg:col-span-2 space-y-6">
                                {/* Product Search */}
                                <div className="relative">
                                    <div className="flex items-center gap-2 bg-white dark:bg-dark-card rounded-lg px-4 py-3 border border-gray-200 dark:border-dark-border shadow-sm">
                                        <Search className="w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Type product name to add..."
                                            className="w-full bg-transparent outline-none dark:text-white"
                                            value={productSearch}
                                            onChange={(e) => setProductSearch(e.target.value)}
                                        />
                                    </div>
                                    {/* Search Results Dropdown */}
                                    {productSearch && filteredProducts.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-xl z-20 max-h-60 overflow-y-auto">
                                            {filteredProducts.map(p => (
                                                <div
                                                    key={p.id}
                                                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer flex justify-between items-center border-b border-gray-100 dark:border-gray-800 last:border-0"
                                                    onClick={() => addToCart(p)}
                                                >
                                                    <div>
                                                        <div className="font-bold text-gray-900 dark:text-white">{p.name}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {p.product_code} | Stock: {p.stock}
                                                            {p.has_variants && <span className="ml-2 text-blue-500 font-bold">(Has Variants)</span>}
                                                        </div>
                                                    </div>
                                                    <Plus className="w-5 h-5 text-blue-500" />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Cart List */}
                                <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Product</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase w-24">Qty</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase w-32">Purchase Price</th>
                                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase w-32">Total</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase w-16"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {cart.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="px-4 py-6 text-center">
                                                        <EmptyState
                                                            preset="up_down"
                                                            title="No products added yet"
                                                            message="Search above to add items to your stock inward cart."
                                                            className="py-6"
                                                        />
                                                    </td>
                                                </tr>
                                            ) : (
                                                cart.map((item, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium text-gray-900 dark:text-white">
                                                                {item.product.name}
                                                                {item.variant && (
                                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                                                        {item.variant.name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {item.variant?.barcode || item.product.product_code}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-bg dark:text-white text-center"
                                                                value={item.quantity}
                                                                onChange={(e) => updateCartItem(idx, 'quantity', e.target.value)}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                className="w-full px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-bg dark:text-white text-right"
                                                                value={item.purchasePrice}
                                                                onChange={(e) => updateCartItem(idx, 'purchasePrice', e.target.value)}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                                                            ₹{(item.quantity * item.purchasePrice).toLocaleString()}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button onClick={() => removeCartItem(idx)} className="text-red-500 hover:text-red-700">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    /* History View */
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
                            {history.length === 0 ? (
                                <div className="p-12 text-center">
                                    <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400">No stock inward history found</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full whitespace-nowrap">
                                        <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                            <tr>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase">Ref No</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase">Supplier Invoice</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase">Supplier</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase">Items</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase">Amount</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase">Payment</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase">Added By</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                        {history.map((po) => (
                                            <tr key={po.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-blue-500" />
                                                        <span className="font-medium text-gray-900 dark:text-white">{po.po_number}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-900 dark:text-white font-mono text-sm">
                                                    {po.invoice_number || "-"}
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                                    {new Date(po.order_date).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                                    {po.supplier_name || "Unknown"}
                                                </td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-700 dark:text-gray-300">
                                                    {po.item_count || 0}
                                                </td>
                                                <td className="px-6 py-4 text-right font-bold text-gray-900 dark:text-white">
                                                    ₹{parseFloat(po.total_amount).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex flex-col items-center">
                                                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                                            po.payment_status === 'paid' ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" :
                                                            po.payment_status === 'partial' ? "bg-blue-100 text-blue-800" :
                                                            "bg-red-100 text-red-800"
                                                        }`}>
                                                            {po.payment_status === 'paid' && <CheckCircle className="w-3 h-3" />}
                                                            {po.payment_status === 'pending' && <Clock className="w-3 h-3" />}
                                                            {po.payment_status.charAt(0).toUpperCase() + po.payment_status.slice(1)}
                                                        </span>
                                                        <span className="text-xs text-gray-500 mt-1 font-mono">
                                                            (₹{(parseFloat(po.paid_amount) || 0).toLocaleString()} / ₹{parseFloat(po.total_amount).toLocaleString()})
                                                        </span>
                                                        {parseFloat(po.total_amount) - (parseFloat(po.paid_amount) || 0) > 0 && (
                                                            <span className="text-[10px] font-bold text-red-500 mt-0.5">
                                                                Bal: ₹{(parseFloat(po.total_amount) - (parseFloat(po.paid_amount) || 0)).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 dark:text-gray-300 text-sm">
                                                    {po.created_by_name || "-"}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center flex-nowrap gap-2 items-center">
                                                        <button
                                                            onClick={() => handleViewItems(po)}
                                                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-full transition-colors"
                                                            title="View Items"
                                                            disabled={loadingView}
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => openEditModal(po)}
                                                            className="text-orange-500 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 p-2 rounded-full transition-colors"
                                                            title="Edit Inward"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(po.id)}
                                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-full transition-colors"
                                                            title="Delete Entry"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
            {/* View Items Modal */}
            {viewModalData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-200 dark:border-dark-border flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Items in {viewModalData.po_number || viewModalData.invoice_number}</h3>
                            <button onClick={() => setViewModalData(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto">
                            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border">
                                    <tr>
                                        <th className="px-4 py-3 font-bold">Product</th>
                                        <th className="px-4 py-3 font-bold text-center">Quantity</th>
                                        <th className="px-4 py-3 font-bold text-right">Price</th>
                                        <th className="px-4 py-3 font-bold text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewModalData.items?.map((item, idx) => (
                                        <tr key={idx} className="bg-white dark:bg-dark-card border-b border-gray-100 dark:border-gray-800">
                                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                                {item.product_name}
                                                {item.variant_name && (
                                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                                        {item.variant_name}
                                                    </span>
                                                )}
                                                <div className="text-xs text-gray-500">{item.product_code}</div>
                                            </td>
                                            <td className="px-4 py-3 text-center font-bold text-gray-900 dark:text-white">{parseFloat(item.quantity)}</td>
                                            <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-300">₹{parseFloat(item.unit_price).toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">₹{parseFloat(item.line_total).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-bg/30">
                            <div className="flex flex-col gap-2 max-w-xs ml-auto">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Total Amount:</span>
                                    <span className="font-bold text-gray-900 dark:text-white text-right">₹{parseFloat(viewModalData.total_amount).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Paid Amount:</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400 text-right">₹{parseFloat(viewModalData.paid_amount || 0).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-t border-gray-200 dark:border-dark-border pt-2">
                                    <span className="font-bold text-gray-700 dark:text-gray-300">Balance Due:</span>
                                    <span className="font-bold text-red-600 dark:text-red-400 text-right">₹{(parseFloat(viewModalData.total_amount) - parseFloat(viewModalData.paid_amount || 0)).toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Inward Modal */}
            {editModalData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl max-w-md w-full overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-200 dark:border-dark-border flex justify-between items-center bg-gray-50 dark:bg-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit {editModalData.po_number}</h3>
                            <button onClick={() => setEditModalData(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Supplier Invoice No</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                                    value={editForm.invoice_number}
                                    onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Paid Amount (₹)</label>
                                <input
                                    type="number"
                                    onWheel={(e) => e.target.blur()}
                                    min="0"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                                    value={editForm.paid_amount}
                                    onChange={(e) => setEditForm({ ...editForm, paid_amount: e.target.value })}
                                />
                            </div>
                            <div className="bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/20">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-red-800 dark:text-red-300 font-bold">Total Bill:</span>
                                    <span className="font-mono font-bold text-red-900 dark:text-red-200">₹{parseFloat(editModalData.total_amount).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm mt-1">
                                    <span className="text-red-800 dark:text-red-300 font-bold">Balance Due:</span>
                                    <span className="font-mono font-bold text-red-600 dark:text-red-400">
                                        ₹{(parseFloat(editModalData.total_amount) - (parseFloat(editForm.paid_amount) || 0)).toLocaleString()}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">Payment Status</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                                    value={editForm.payment_status}
                                    onChange={(e) => setEditForm({ ...editForm, payment_status: e.target.value })}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="partial">Partial</option>
                                    <option value="paid">Paid</option>
                                </select>
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-dark-border flex justify-end gap-3 bg-gray-50 dark:bg-gray-800">
                            <button onClick={() => setEditModalData(null)} className="px-4 py-2 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">Cancel</button>
                            <button onClick={handleSaveEdit} disabled={savingEdit} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center gap-2 transition-colors">
                                {savingEdit ? "Saving..." : <><Save className="w-4 h-4" /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Variant Selection Modal */}
            {variantModalProduct && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                    >
                        <div className="p-6 border-b border-gray-100 dark:border-dark-border flex justify-between items-center bg-blue-50/50 dark:bg-dark-bg/50">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Select Variant</h3>
                                <p className="text-sm text-gray-500">{variantModalProduct.name}</p>
                            </div>
                            <button onClick={() => setVariantModalProduct(null)} className="p-2 hover:bg-gray-200 dark:hover:bg-dark-border rounded-full transition-colors">
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>
                        
                        <div className="p-4 grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto">
                            {variantModalProduct.variants.map(variant => (
                                <button
                                    key={variant.id}
                                    onClick={() => addToCart(variantModalProduct, variant)}
                                    className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 dark:border-dark-border hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-left group"
                                >
                                    <div>
                                        <div className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">{variant.name}</div>
                                        <div className="text-xs text-gray-500">
                                            {variant.barcode || "No Barcode"} | Stock: {variant.stock}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-bold text-green-600 dark:text-green-400">₹{variant.unit_price}</div>
                                        <Plus className="w-5 h-5 text-blue-500 ml-auto mt-1" />
                                    </div>
                                </button>
                            ))}
                        </div>
                        
                        <div className="p-4 bg-gray-50 dark:bg-dark-bg/30 border-t border-gray-100 dark:border-dark-border">
                            <button 
                                onClick={() => setVariantModalProduct(null)}
                                className="w-full py-3 text-gray-600 dark:text-gray-400 font-bold hover:bg-gray-200 dark:hover:bg-dark-border rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Success Confirmation Modal */}
            {successModalData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 dark:border-dark-border"
                    >
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-green-50 dark:bg-green-900/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-100 dark:border-green-900/20">
                                <CheckCircle className="w-8 h-8 text-green-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Stock Inward Successful!</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                Stock has been added successfully. <br />
                                <span className="font-mono font-semibold bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200 mt-2 inline-block">
                                    PO Number: {successModalData.po_number}
                                </span>
                            </p>
                            
                            <div className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-6">
                                Do you want to add another entry?
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => {
                                        setCart([]);
                                        setInvoiceNumber("");
                                        setSelectedSupplier("");
                                        setSuccessModalData(null);
                                        setActiveTab("history");
                                    }}
                                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-dark-border dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setCart([]);
                                        setInvoiceNumber("");
                                        setSelectedSupplier("");
                                        setSuccessModalData(null);
                                    }}
                                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 flex items-center justify-center gap-2 cursor-pointer text-sm"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Custom Alert Modal */}
            {alertMessage && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-gray-100 dark:border-dark-border"
                    >
                        <div className="p-6 text-center">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Notification</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 font-medium">
                                {alertMessage}
                            </p>
                            <button
                                onClick={() => setAlertMessage(null)}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 cursor-pointer text-sm"
                            >
                                OK
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
